import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DmsBadge, DocHead, Panel, Section } from "../sales/dmsUi";
import { AppDropdown, EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";
import { CLAIM_TYPES, EditClaimDrawer, NewClaimDrawer, NoClaimsHelp } from "./ClaimForms";

// Warranty Claims: warranty work from invoiced repair orders, billed to the manufacturer.
// Submit → the manufacturer's decision (a shortfall is credited and written off) → settled when paid.
const STATUSES = ["DRAFT", "SUBMITTED", "APPROVED", "PARTIALLY_APPROVED", "REJECTED", "SETTLED"];
const label = (s) => String(s || "").charAt(0) + String(s || "").slice(1).toLowerCase().replace(/_/g, " ");

function ClaimView({ id, onHide, onChanged }) {
  const { data: c, loading, error, reload } = useLoad(() => dmsService.getWarrantyClaim(id), [id]);
  const [submitting, setSubmitting] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [editing, setEditing] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <Panel title={c ? `Warranty claim ${c.claim_number}` : "Warranty claim"} onHide={onHide} width={860}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {c ? (
          <>
            <DocHead
              title=""
              number={c.make_name || "Manufacturer"}
              status={c.status}
              sub={`${c.ro ? `${c.ro.ro_number} · ${c.ro.customer_name} · ${c.ro.vehicle_desc || ""} ${c.ro.plate_no || ""}` : ""}${c.vin ? ` · VIN ${c.vin}` : ""}${c.oem_reference ? ` · OEM ref ${c.oem_reference}` : ""}`}
            >
              {c.status === "DRAFT" ? <SecondaryButton onClick={() => setEditing(true)}>Edit lines</SecondaryButton> : null}
              {c.status === "DRAFT" ? <PrimaryButton onClick={() => setSubmitting(true)}>Submit to manufacturer</PrimaryButton> : null}
              {c.status === "SUBMITTED" ? <PrimaryButton onClick={() => setDeciding(true)}>Record decision</PrimaryButton> : null}
            </DocHead>
            {c.claim_type && c.claim_type !== "REPAIR" ? (
              <div className="mb-3" style={{ fontSize: 14 }}>
                <b>{CLAIM_TYPES[c.claim_type]} claim.</b> {c.reason}
              </div>
            ) : null}
            <Section title="Claimed">
              <DataTable
                rows={c.lines}
                rowKey={(l) => l.id}
                columns={[
                  { label: "Job", render: (l) => l.job_no || "" },
                  { label: "Kind", render: (l) => label(l.line_kind) },
                  { label: "Description", render: (l) => l.description },
                  { label: "Qty", num: true, render: (l) => Number(l.quantity) },
                  { label: "Amount", num: true, render: (l) => fmtMoney(l.amount) },
                ]}
              />
              <div className="mt-2" style={{ fontSize: 14 }}>
                Claimed <b>{fmtMoney(c.claimed_amount)}</b>
                {c.approved_amount !== null ? (
                  <>
                    {" "}
                    · approved <b>{fmtMoney(c.approved_amount)}</b>
                  </>
                ) : null}
                {c.rejection_reason ? <div style={hint}>Shortfall reason: {c.rejection_reason}</div> : null}
              </div>
              {c.invoice ? (
                <div className="mt-2" style={{ fontSize: 14 }}>
                  Billed to {c.oem_account ? `${c.oem_account.name} (${c.oem_account.account_number})` : "the manufacturer"} on {c.invoice.trx_number}: {fmtMoney(c.invoice.amount_due_remaining)} still to collect{" "}
                  <button type="button" style={linkBtn} onClick={() => finService.transactionPdf(c.ar_trx_id, `${c.invoice.trx_number}.pdf`)}>
                    Invoice
                  </button>
                </div>
              ) : null}
            </Section>
            <Section title="Action history">
              <ActionHistory actions={c.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      <EditClaimDrawer claim={c} show={editing} onHide={() => setEditing(false)} onDone={refresh} />
      <FormDrawer
        show={submitting}
        title="Submit to the manufacturer"
        initial={{ oem_account_number: c ? c.default_account_number || "" : "", oem_reference: "" }}
        onHide={() => setSubmitting(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.warrantyClaimAction(id, "submit", clean)}
        fields={[
          { key: "oem_account_number", label: "Manufacturer's customer account number", max: 30, hint: "From the make; change it only for this claim" },
          { key: "oem_reference", label: "Manufacturer's claim reference", max: 60 },
        ]}
      />
      <FormDrawer
        show={deciding}
        title="Manufacturer's decision"
        initial={{ approved_amount: c ? Number(c.claimed_amount) : "", rejection_reason: "", oem_reference: c ? c.oem_reference || "" : "" }}
        onHide={() => setDeciding(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.warrantyClaimAction(id, "decision", { ...clean, approved_amount: Number(clean.approved_amount) })}
        fields={[
          { key: "approved_amount", label: "Amount approved", type: "number", step: "any", min: 0, required: true },
          { key: "rejection_reason", label: "Reason for any shortfall", max: 500 },
          { key: "oem_reference", label: "Manufacturer's reference", max: 60 },
        ]}
      />
    </Panel>
  );
}

// Failed parts replaced under warranty, kept (tagged) until the manufacturer releases them or asks
// for them to be shipped back.
const RETAINED = { RETAINED: "Kept", REQUESTED: "Requested by the manufacturer", SHIPPED: "Shipped", RELEASED: "Released", SCRAPPED: "Scrapped" };
const RETAINED_NEXT = { RETAINED: ["REQUESTED", "SHIPPED", "RELEASED"], REQUESTED: ["SHIPPED", "RELEASED"], RELEASED: ["SCRAPPED"] };

function RetainedParts() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listRetainedParts({ status: status || undefined, q: q || undefined }), [status, q]);
  const [moving, setMoving] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="Tag, part, VIN, RO or claim" aria-label="Search retained parts" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ width: 240 }}>
          <AppDropdown id="rp-st" value={status} options={[{ label: "All", value: "" }, ...Object.entries(RETAINED).map(([value, l]) => ({ label: l, value }))]} onChange={(e) => setStatus(e.value)} />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No failed parts kept" text="Every part replaced on a warranty job is tagged here." />}
          columns={[
            { label: "Tag", render: (r) => <b>{r.tag_number}</b> },
            { label: "Part", render: (r) => `${r.part_number || ""} ${r.description}` },
            { label: "Serial", render: (r) => r.serial_no || "" },
            { label: "RO / job", render: (r) => `${r.ro_number} / ${r.job_no}` },
            { label: "Claim", render: (r) => r.claim_number || "" },
            { label: "VIN", render: (r) => r.vin || "" },
            { label: "Bin", render: (r) => r.storage_bin || "" },
            { label: "Keep until", render: (r) => <span style={{ color: r.retain_until && String(r.retain_until).slice(0, 10) < today && ["RETAINED", "REQUESTED"].includes(r.status) ? "#B91C1C" : undefined }}>{r.retain_until ? formatDate(r.retain_until) : ""}</span> },
            { label: "Status", render: (r) => RETAINED[r.status] || r.status },
            { label: "", render: (r) => (RETAINED_NEXT[r.status] ? <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setMoving(r)}>Update</button> : r.shipment_ref || "") },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!moving}
        title={moving ? `Tag ${moving.tag_number}` : ""}
        initial={moving ? { status: (RETAINED_NEXT[moving.status] || [])[0], shipment_ref: "", note: "" } : {}}
        onHide={() => setMoving(null)}
        onDone={reload}
        onSave={(clean) => dmsService.updateRetainedPart(moving.id, clean)}
        fields={[
          { key: "status", label: "Now", type: "select", required: true, options: moving ? (RETAINED_NEXT[moving.status] || []).map((v) => ({ label: RETAINED[v], value: v })) : [] },
          { key: "shipment_ref", label: "Shipment / airway bill (when shipped)", max: 60 },
          { key: "note", label: "Note", max: 240 },
        ]}
      />
    </>
  );
}

export default function WarrantyClaims() {
  const [tab, setTab] = useState("claims");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listWarrantyClaims({ status: status || undefined, q: q || undefined }), [status, q]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const filtered = !!(status || q);
  return (
    <div style={pageStyle}>
      <PageHeader title="Warranty Claims" subtitle="Warranty work billed to the manufacturer, and the failed parts kept for it" actions={tab === "claims" ? <PrimaryButton onClick={() => setCreating(true)}>New claim</PrimaryButton> : null} />
      <Tabs tabs={[{ key: "claims", label: "Claims" }, { key: "retained", label: "Retained parts" }]} active={tab} onChange={setTab} />
      {tab === "retained" ? <RetainedParts /> : null}
      {tab === "claims" ? (
      <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="Claim, RO or VIN" aria-label="Search claims" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ width: 220 }}>
          <AppDropdown id="wc-st" value={status} options={[{ label: "All", value: "" }, ...STATUSES.map((s) => ({ label: label(s), value: s }))]} onChange={(e) => setStatus(e.value)} />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={filtered ? <EmptyState title="No claims match" text="Clear the search or the status to see every claim." /> : <NoClaimsHelp onNew={() => setCreating(true)} />}
          columns={[
            { label: "Claim", render: (r) => (
                <>
                  <b>{r.claim_number}</b>
                  {r.claim_type && r.claim_type !== "REPAIR" ? <div style={hint}>{CLAIM_TYPES[r.claim_type]}</div> : null}
                </>
              ) },
            { label: "RO", render: (r) => r.ro_number },
            { label: "Make", render: (r) => r.make_name || "" },
            { label: "VIN", render: (r) => r.vin || "" },
            { label: "Submitted", render: (r) => (r.submitted_at ? formatDate(r.submitted_at) : "") },
            { label: "Claimed", num: true, render: (r) => fmtMoney(r.claimed_amount) },
            { label: "Approved", num: true, render: (r) => (r.approved_amount === null ? "" : fmtMoney(r.approved_amount)) },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      </>
      ) : null}
      <NewClaimDrawer show={creating} onHide={() => setCreating(false)} onDone={(d) => { reload(); if (d && d.id) setOpen(d.id); }} />
      {open ? <ClaimView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
    </div>
  );
}
